import { ArrowUpRight } from 'lucide-react';
import { ReactNode, useState } from 'react';

interface CTAPrimaryButtonProps {
  children: ReactNode;
  showArrow?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  useWhiteText?: boolean;
}

export default function CTAPrimaryButton({
  children,
  showArrow = false,
  href,
  onClick,
  className = '',
  disabled = false,
  useWhiteText = false,
}: CTAPrimaryButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Color scheme based on background
  const textColor = useWhiteText ? 'text-white' : 'text-[#1B1B1B]';
  const borderColor = useWhiteText ? 'border-white' : 'border-[#1B1B1B]';
  
  const baseClasses = `inline-flex items-center gap-1 border px-4 py-2 rounded-full label-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-300 ease-out ${borderColor} hover:bg-[#E60000] hover:border-[#E60000] `;
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const combinedClasses = `${baseClasses} ${disabledClasses} ${className}`;

  const content = (
    <>
      <div className="cta-primary-button relative overflow-hidden px-2" style={{ willChange: 'transform' }}>
        <span 
          className={`block transition-transform duration-300 ease-out ${textColor} ${
            isHovered ? '-translate-y-full' : 'translate-y-0'
          }`}
          style={{ willChange: 'transform' }}
        >
          {children}
        </span>
        <span 
          className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out text-eggshell ${
            isHovered ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ willChange: 'transform' }}
        >
          {children}
        </span>
      </div>
      {showArrow && (
        <ArrowUpRight 
          className={`w-8 h-8 transition-transform duration-300 ease-out rotate-45 ${
            isHovered 
              ? 'translate-x-1.5' 
              : 'translate-x-0'
          }`}
          style={{ 
            willChange: 'transform',
            transition: 'transform 300ms ease-out',
            color: isHovered 
              ? '#FDFEF6' // eggshell
              : useWhiteText 
                ? '#FFFFFF' // white
                : '#1B1B1B' // dark
          }}
          strokeWidth={1}
        />
      )}
    </>
  );

  if (href) {
    return (
      <a 
        href={href} 
        className={combinedClasses}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={combinedClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
    </button>
  );
}